import { FC, useState, useCallback, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { TextField, Box, Menu, MenuItem, useTheme, InputAdornment } from '@mui/material'
import { GenericMenu, MenuItemProps } from 'src/components/common/menu/GenericMenu'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { FacilityTypes } from 'src/enums'
import {
  INewsFacilityFilterProps,
  buildFilters,
  getFilterTypeLabel,
  getParentMenuItems,
  createItemHandlers,
  mapItems,
  getSelectedFilterState,
} from './NewsFacilityFilter.action'

const NewsFacilityFilter: FC<INewsFacilityFilterProps> = ({
  customerId,
  facilityFilters,
  onFiltersChange,
  localitiesData,
  serviceStationsData,
  storagesData,
  localityGroupsData,
  isLoadingLocalities,
  isLoadingServiceStations,
  isLoadingStorages,
  isLoadingLocalityGroups,
}) => {
  const { t: translate } = useTranslation(['common', 'news', 'page-container'])
  const theme = useTheme()

  const [selectedFilterType, setSelectedFilterType] = useState<FacilityTypes | ''>('')

  const isLoadingSubmenu = useMemo(() => {
    if (selectedFilterType === FacilityTypes.locality) return isLoadingLocalities
    if (selectedFilterType === FacilityTypes.serviceStation) return isLoadingServiceStations
    if (selectedFilterType === FacilityTypes.storage) return isLoadingStorages
    if (selectedFilterType === FacilityTypes.localityGroup) return isLoadingLocalityGroups
    return false
  }, [selectedFilterType, isLoadingLocalities, isLoadingServiceStations, isLoadingStorages, isLoadingLocalityGroups])

  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const [parentAnchorEl, setParentAnchorEl] = useState<null | HTMLElement>(null)
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState<null | HTMLElement>(null)
  const [textFieldWidth, setTextFieldWidth] = useState<number>(0)

  // Get display name for selected filter
  const filterLabel = useMemo(() => getFilterTypeLabel(translate, facilityFilters), [translate, facilityFilters])

  // Facility types for parent menu
  const parentMenuItems = useMemo(() => {
    const items = getParentMenuItems(translate, facilityFilters)
    return items
      .filter(item => {
        if (item.id === FacilityTypes.locality) return localitiesData && localitiesData.length > 0
        if (item.id === FacilityTypes.serviceStation) return serviceStationsData && serviceStationsData.length > 0
        if (item.id === FacilityTypes.storage) return storagesData && storagesData.length > 0
        if (item.id === FacilityTypes.localityGroup) return localityGroupsData && localityGroupsData.length > 0
        return true
      })
      .map(item => ({
        id: item.id,
        label: item.label,
        onClick: () => {
          setSelectedFilterType(item.id)
          setSelectedItems(item.getSelectedItems())
        },
      }))
  }, [translate, facilityFilters, localitiesData, serviceStationsData, storagesData, localityGroupsData])

  // Clear selected filter type if it no longer has data
  useEffect(() => {
    if (selectedFilterType === '') return

    const hasData = parentMenuItems.some(item => item.id === selectedFilterType)
    if (!hasData) {
      setSelectedFilterType('')
      setSubmenuAnchorEl(null)
    }
  }, [parentMenuItems, selectedFilterType])

  const buildFiltersFunc = useCallback(
    (filterType: FacilityTypes | '', items: number[]) => {
      buildFilters(filterType, items)
      onFiltersChange(buildFilters(filterType, items))
    },
    [onFiltersChange],
  )

  const createHandlers = useCallback(
    (itemId: number) =>
      createItemHandlers(itemId, selectedFilterType, selectedItems, setSelectedItems, onFiltersChange),
    [selectedFilterType, selectedItems, onFiltersChange],
  )

  // Submenu items based on selected filter type
  const submenuItems = useMemo<MenuItemProps[]>(() => {
    let sourceItems: Array<{ id: number; name: string }> | undefined
    let allLabel = ''

    if (selectedFilterType === FacilityTypes.locality && localitiesData) {
      sourceItems = localitiesData
      allLabel = translate('all-localities', { ns: 'common' })
    } else if (selectedFilterType === FacilityTypes.serviceStation && serviceStationsData) {
      sourceItems = serviceStationsData
      allLabel = translate('all-service-stations', { ns: 'common' })
    } else if (selectedFilterType === FacilityTypes.storage && storagesData) {
      sourceItems = storagesData
      allLabel = translate('all-storages', { ns: 'common' })
    } else if (selectedFilterType === FacilityTypes.localityGroup && localityGroupsData) {
      sourceItems = localityGroupsData
      allLabel = translate('all-locality-groups', { ns: 'common' })
    }

    if (!sourceItems) return []

    const allIds = sourceItems.map(item => item.id)
    const allSelected = allIds.length > 0 && allIds.every(id => selectedItems.includes(id))

    const allItem: MenuItemProps = {
      id: 'all',
      label: allLabel,
      checked: allSelected,
      onCheckBoxChange: (_event, checked) => {
        const newItems = checked ? allIds : []
        setSelectedItems(newItems)
        buildFiltersFunc(selectedFilterType, newItems)
      },
      onClick: (event: React.MouseEvent) => {
        const target = event.target as HTMLElement
        const isCheckboxClick = target instanceof HTMLInputElement && target.type === 'checkbox'
        if (!isCheckboxClick) {
          const newItems = allSelected ? [] : allIds
          setSelectedItems(newItems)
          buildFiltersFunc(selectedFilterType, newItems)
        }
      },
    }

    return [allItem, ...mapItems(sourceItems, selectedItems, createHandlers)]
  }, [
    selectedFilterType,
    selectedItems,
    localitiesData,
    serviceStationsData,
    storagesData,
    localityGroupsData,
    translate,
    buildFiltersFunc,
    createHandlers,
  ])

  const handleApplyFilters = useCallback(() => {
    setSubmenuAnchorEl(null)
  }, [])

  const handleParentMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setParentAnchorEl(event.currentTarget)
    const width = event.currentTarget.offsetWidth
    setTextFieldWidth(width)

    const { filterType, items } = getSelectedFilterState(facilityFilters)
    setSelectedFilterType(filterType)
    setSelectedItems(items)
  }

  return (
    <Box>
      <TextField
        size="small"
        label={translate('facility', { ns: 'news' })}
        value={filterLabel}
        onClick={handleParentMenuClick}
        inputProps={{ readOnly: true }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <ArrowDropDownIcon />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root:hover': {
            cursor: 'pointer !important',
          },
          '& .MuiOutlinedInput-input': {
            cursor: 'pointer !important',
          },
        }}
        fullWidth
      />

      <Menu
        anchorEl={parentAnchorEl}
        open={Boolean(parentAnchorEl)}
        onClose={() => setParentAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: textFieldWidth,
          },
        }}
      >
        {parentMenuItems.map(item => (
          <MenuItem
            key={item.id}
            onClick={event => {
              event.stopPropagation()
              item.onClick()
              setSubmenuAnchorEl(event.currentTarget)
            }}
            sx={{
              backgroundColor: selectedFilterType === item.id ? theme.palette.secondary.light : 'transparent',
              '&:hover': {
                backgroundColor: selectedFilterType === item.id ? theme.palette.secondary.light : undefined,
              },
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      <GenericMenu
        isOpen={Boolean(submenuAnchorEl)}
        anchorEl={submenuAnchorEl}
        onClose={handleApplyFilters}
        menuItems={submenuItems}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        isLoading={isLoadingSubmenu}
      />
    </Box>
  )
}

export default NewsFacilityFilter
